import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AccountingHealth } from "@/api/accounting";
import { seedAccountingDefaults } from "@/api/accounting";
import { Button } from "@/components/ui/button";
import { useUnifiedErrorSurface } from "@/hooks/useUnifiedErrorSurface";

type Props = {
  open: boolean;
  health: AccountingHealth | null;
  loading?: boolean;
  onClose: () => void;
  onRefresh: () => Promise<void> | void;
};

type WizardStep = {
  id: string;
  title: string;
  description: string;
  issues: string[];
  cta?: { label: string; onClick: () => void; disabled?: boolean };
};

export function AccountingSetupWizard({ open, health, loading, onClose, onRefresh }: Props) {
  const navigate = useNavigate();
  const { notifyError } = useUnifiedErrorSurface();
  const [seeding, setSeeding] = useState(false);

  const shopifyIssues = useMemo(() => {
    if (!open || !health) return [];
    return Object.values(health.shopify || {}).flatMap((issue) =>
      issue.issues.map((text) => `${issue.label}: ${text}`),
    );
  }, [open, health]);

  const bankIssues = useMemo(() => {
    if (!open || !health) return [];
    return Object.values(health.bank || {}).flatMap((issue) =>
      issue.issues.map((text) => `${issue.label}: ${text}`),
    );
  }, [open, health]);

  const managerIssues = useMemo(() => {
    if (!open || !health) return [];
    return Object.values(health.events || {}).flatMap((issue) =>
      issue.issues.map((text) => `${issue.label}: ${text}`),
    );
  }, [open, health]);

  if (!open) return null;

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await seedAccountingDefaults();
      await onRefresh();
    } catch (error) {
      notifyError(error, "Seed defaults failed");
    } finally {
      setSeeding(false);
    }
  };

  const steps: WizardStep[] = [
    {
      id: "canonical",
      title: "Canonical ledger accounts",
      description: "Create the recommended chart of accounts before posting entries.",
      issues: health?.missing ?? [],
      cta: {
        label: seeding ? "Seeding…" : "Add recommended accounts",
        onClick: handleSeed,
        disabled: seeding,
      },
    },
    {
      id: "shopify",
      title: "Shopify mappings",
      description: "Each shop needs bank, sales, fee, and tax accounts.",
      issues: shopifyIssues,
      cta: {
        label: "Open Shopify panel",
        onClick: () => {
          onClose();
          navigate("/accounting?tab=shopify");
        },
      },
    },
    {
      id: "bank",
      title: "Bank reconciliations",
      description: "Link every bank account to a ledger account so imports can post.",
      issues: bankIssues,
      cta: {
        label: "Open Bank panel",
        onClick: () => {
          onClose();
          navigate("/accounting?tab=bank");
        },
      },
    },
    {
      id: "events",
      title: "Manager defaults",
      description: "Ensure event settlements have revenue and expense mappings.",
      issues: managerIssues,
      cta: {
        label: "Review events",
        onClick: () => {
          onClose();
          navigate("/manager");
        },
      },
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 px-4 py-6">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl border border-border/80 bg-background/95 p-6 shadow-2xl">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">Accounting setup wizard</p>
            <h2 className="text-2xl font-semibold text-primary-foreground dark:text-amber-100">Complete your ledger mappings</h2>
            <p className="text-sm text-muted-foreground">
              Follow these steps to finish configuring journals, accounts, Shopify, banking, and manager workflows.
            </p>
          </div>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {steps.map((step) => (
            <section
              key={step.id}
              className="rounded-2xl border border-border/80 bg-background/30 p-4 text-sm text-foreground/80"
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-100">{step.title}</h3>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
                <span
                  className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                    step.issues.length ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"
                  }`}
                >
                  {step.issues.length ? `${step.issues.length} issues` : "Ready"}
                </span>
              </div>
              {step.issues.length > 0 && (
                <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-foreground/70">
                  {step.issues.map((issue) => (
                    <li key={issue}>{issue}</li>
                  ))}
                </ul>
              )}
              {step.cta && (
                <Button
                  type="button"
                  variant="secondary"
                  className="mt-3"
                  onClick={step.cta.onClick}
                  disabled={step.cta.disabled}
                >
                  {step.cta.label}
                </Button>
              )}
            </section>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-background/30 px-4 py-3 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Current status</p>
            <p className="text-base font-semibold text-primary-foreground dark:text-amber-100">
              {loading
                ? "Checking configuration…"
                : health?.ok
                  ? "All set — accounting mappings look healthy."
                  : "Fix the highlighted steps to enable ledger automations."}
            </p>
          </div>
          {!health?.ok && (
            <Button variant="outline" onClick={handleSeed} disabled={seeding}>
              {seeding ? "Applying…" : "Re-run defaults"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
